import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, deleteDoc, query, orderBy, onSnapshot, where } from '../firebase.config';
import { db } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { getAuth } from 'firebase/auth';
import { Link } from 'react-router-dom';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';

const ReportedIssues = () => {
  const { currentCompany, employees } = useEnhancedCompany();
  const auth = getAuth();
  const currentUser = auth.currentUser;

  const [reports, setReports] = useState([]);
  const [filteredReports, setFilteredReports] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [loading, setLoading] = useState(true);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replying, setReplying] = useState(false);
  // Load all reports for the company
  useEffect(() => {
    if (!currentCompany?.companyId) return;

    const reportsRef = collection(db, 'companies', currentCompany.companyId, 'reports');
    // Remove orderBy to avoid index requirement for now
    const q = query(reportsRef);

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const reportsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt locally to avoid index requirement
      reportsData.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
      
      // Resolved issues are now directly transferred to resolved list
      // No more 24-hour timer - they stay in resolved status
      
      // Filter out resolved issues from reported issues list
      const nonResolvedReports = reportsData.filter(report => report.status !== 'resolved');

      setReports(nonResolvedReports);
      setFilteredReports(nonResolvedReports);
      setLoading(false);
    }, (error) => {
      console.error('Error loading reports:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentCompany?.companyId]);

  // Filter reports by employee (already filtered for non-resolved)
  useEffect(() => {
    if (selectedEmployee === '') {
      setFilteredReports(reports);
    } else {
      const filtered = reports.filter(report => report.userId === selectedEmployee);
      setFilteredReports(filtered);
    }
  }, [selectedEmployee, reports]);

  // Get employee name by userId
  const getEmployeeName = (userId) => {
    const employee = employees?.find(emp => emp.userId === userId);
    return employee ? employee.name : 'Unknown Employee';
  };

  // Countdown functionality removed - resolved issues stay resolved

  // Handle reply submission
  const handleReply = async (reportId) => {
    if (!replyMessage.trim()) return;

    setReplying(true);
    try {
      const reportRef = doc(db, 'companies', currentCompany.companyId, 'reports', reportId);
      await updateDoc(reportRef, {
        ownerReply: replyMessage,
        ownerReplyAt: new Date(),
        status: 'in_progress',
        updatedAt: new Date()
      });

      setReplyingTo(null);
      setReplyMessage('');
    } catch (error) {
      console.error('Error sending reply:', error);
      alert('Error sending reply. Please try again.');
    } finally {
      setReplying(false);
    }
  };

  // Handle delete report
  const handleDelete = async (reportId) => {
    if (!window.confirm('Are you sure you want to delete this report? This action cannot be undone.')) return;

    try {
      await deleteDoc(doc(db, 'companies', currentCompany.companyId, 'reports', reportId));
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Error deleting report. Please try again.');
    }
  };

  // Handle status change
  const handleStatusChange = async (reportId, newStatus) => {
    try {
      const reportRef = doc(db, 'companies', currentCompany.companyId, 'reports', reportId);
      await updateDoc(reportRef, {
        status: newStatus,
        updatedAt: new Date()
      });
      
      // If status changed to resolved, it will automatically disappear from reported issues list
      // due to the filter logic in useEffect
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status. Please try again.');
    }
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'escalated': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Get unread count for current user (owner)
  const getUnreadCount = () => {
    return reports.filter(report => !report.isRead).length;
  };

  // Mark report as read when owner views it
  const markAsRead = async (reportId) => {
    const report = reports.find(r => r.id === reportId);
    if (report && !report.isRead) {
      try {
        const reportRef = doc(db, 'companies', currentCompany.companyId, 'reports', reportId);
        await updateDoc(reportRef, {
          isRead: true,
          updatedAt: new Date()
        });
      } catch (error) {
        console.error('Error marking as read:', error);
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Reported Issues</h1>
            <p className="text-gray-600">Manage and respond to issues reported by employees</p>
          </div>
          <Link
            to="/resolved-issues"
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors"
          >
            View Resolved Issues
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-blue-600">{reports.length}</div>
            <div className="text-sm text-gray-600">Total Reports</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-yellow-600">
              {reports.filter(r => r.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-600">Pending</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-blue-600">
              {reports.filter(r => r.status === 'in_progress').length}
            </div>
            <div className="text-sm text-gray-600">In Progress</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <div className="text-2xl font-bold text-green-600">
              {reports.filter(r => r.status === 'resolved').length}
            </div>
            <div className="text-sm text-gray-600">Resolved</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter by Employee
            </label>
            <AppSelect
              value={selectedEmployee}
              onChange={(e) => setSelectedEmployee(e.target.value)}
              aria-label="Filter by employee"
              sheetTitle="Filter by employee"
              options={[
                { value: '', label: 'All Employees' },
                ...[...new Set(reports.map(r => r.userId))].map(userId => ({
                  value: userId,
                  label: getEmployeeName(userId),
                })),
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER}`}
            />
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredReports.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            {selectedEmployee ? 'No reports found for this employee' : 'No Issues Found'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Page/Feature
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Issue Details
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredReports.map((report) => (
                  <tr key={report.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {getEmployeeName(report.userId)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {report.userId.slice(0, 8)}...
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {report.page}
                      {report.subCategory && (
                        <div className="text-xs text-gray-500">{report.subCategory}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                      {report.billNo && <div className="font-medium">Bill: {report.billNo}</div>}
                      {report.reportType && <div className="font-medium">Report: {report.reportType}</div>}
                      {report.message && (
                        <div className="text-xs text-gray-600 mt-1 truncate">
                          {report.message}
                        </div>
                      )}
                      {report.ownerReply && (
                        <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-50 rounded">
                          <strong>Your Reply:</strong> {report.ownerReply}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <AppSelect
                        value={report.status}
                        onChange={(e) => handleStatusChange(report.id, e.target.value)}
                        aria-label="Report status"
                        sheetTitle="Report status"
                        options={[
                          { value: 'pending', label: 'Pending' },
                          { value: 'in_progress', label: 'In Progress' },
                          { value: 'resolved', label: 'Resolved' },
                          { value: 'escalated', label: 'Escalated' },
                        ]}
                        triggerClassName={`text-xs font-semibold rounded-full px-2 py-1 border-0 min-h-[28px] flex items-center justify-between gap-1 touch-manipulation ${getStatusColor(report.status)}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {report.status === 'resolved' ? (
                        <span className="text-green-600 font-medium">Resolved</span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(report.createdAt.seconds * 1000).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-y-2">
                      {!report.ownerReply && (
                        <div>
                          {replyingTo === report.id ? (
                            <div className="space-y-2">
                              <textarea
                                value={replyMessage}
                                onChange={(e) => setReplyMessage(e.target.value)}
                                placeholder="Type your reply..."
                                rows={3}
                                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleReply(report.id)}
                                  disabled={replying || !replyMessage.trim()}
                                  className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 disabled:bg-gray-400"
                                >
                                  {replying ? 'Sending...' : 'Send Reply'}
                                </button>
                                <button
                                  onClick={() => {
                                    setReplyingTo(null);
                                    setReplyMessage('');
                                  }}
                                  className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setReplyingTo(report.id);
                                markAsRead(report.id);
                              }}
                              className="text-blue-600 hover:text-blue-900 block"
                            >
                              Reply
                            </button>
                          )}
                        </div>
                      )}
                      <button
                        onClick={() => handleDelete(report.id)}
                        className="text-red-600 hover:text-red-900 block"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportedIssues;
